/**
 * Profile card: the session registry.
 *
 * Lists the account's live sessions (one per device/login), supports inline
 * rename, trust-tier changes (which re-issue the session deadline at the new
 * TTL), revoking a single session, and "Sign out all other devices" — the
 * mitigation that makes 90-day trusted sessions safe.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';
import { SessionInfo, TrustTier, TRUST_TIER_LABELS } from '../auth/tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TIER_CHIP_COLOR: Record<TrustTier, 'default' | 'primary' | 'success'> = {
  untrusted: 'default',
  moderate: 'primary',
  trusted: 'success',
};

const formatWhen = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

const SessionsSection: React.FC = () => {
  const { cardContentPaddingSx } = useLayoutTokens();
  const { logout } = useAuth();
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/auth/sessions`);
      setSessions(response.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const startRename = (session: SessionInfo) => {
    setRenamingId(session.id);
    setRenameValue(session.device_label || '');
  };

  const commitRename = async (sessionId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/auth/sessions/${sessionId}`, {
        device_label: renameValue,
      });
      setRenamingId(null);
      await fetchSessions();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to rename session'));
    }
  };

  const changeTier = async (session: SessionInfo, tier: TrustTier) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/auth/sessions/${session.id}`, {
        trust_tier: tier,
      });
      await fetchSessions();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to change trust level'));
    }
  };

  const revokeSession = async (session: SessionInfo) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/auth/sessions/${session.id}`);
      if (session.is_current) {
        logout();
        return;
      }
      await fetchSessions();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to revoke session'));
    }
  };

  const revokeOthers = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/auth/sessions`);
      await fetchSessions();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to sign out other devices'));
    }
  };

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Sessions</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={revokeOthers}
            disabled={sessions.filter((s) => !s.is_current).length === 0}
          >
            Sign out all other devices
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Devices signed in to your account. The trust level sets how long a
          device stays signed in without re-entering your password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={28} />
          </Box>
        ) : sessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No live sessions found.
          </Typography>
        ) : (
          sessions.map((session) => (
            <Box
              key={session.id}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                {renamingId === session.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={renameValue}
                      onChange={(event) => setRenameValue(event.target.value)}
                      inputProps={{ 'aria-label': 'Device name', maxLength: 120 }}
                      autoFocus
                    />
                    <Tooltip title="Save name">
                      <IconButton
                        size="small"
                        aria-label="Save device name"
                        onClick={() => commitRename(session.id)}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        aria-label="Cancel rename"
                        onClick={() => setRenamingId(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {session.device_label || 'Unnamed device'}
                    </Typography>
                    <Tooltip title="Rename">
                      <IconButton
                        size="small"
                        aria-label={`Rename ${session.device_label || 'unnamed device'}`}
                        onClick={() => startRename(session)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {session.is_current && (
                      <Chip label="This device" size="small" color="info" />
                    )}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  Last active {formatWhen(session.last_seen_at)} · Expires{' '}
                  {formatWhen(session.expires_at)}
                </Typography>
              </Box>
              <Chip
                label={TRUST_TIER_LABELS[session.trust_tier]}
                size="small"
                color={TIER_CHIP_COLOR[session.trust_tier]}
                variant="outlined"
              />
              <Select
                size="small"
                value={session.trust_tier}
                onChange={(event) => changeTier(session, event.target.value as TrustTier)}
                inputProps={{ 'aria-label': `Trust level for ${session.device_label || 'unnamed device'}` }}
              >
                {(Object.keys(TRUST_TIER_LABELS) as TrustTier[]).map((tier) => (
                  <MenuItem key={tier} value={tier}>
                    {TRUST_TIER_LABELS[tier]}
                  </MenuItem>
                ))}
              </Select>
              <Tooltip title={session.is_current ? 'Sign out this device' : 'Revoke session'}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Revoke ${session.device_label || 'unnamed device'}`}
                  onClick={() => revokeSession(session)}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default SessionsSection;
